import React, { useEffect, useState } from 'react';
import axiosClient from '../../api/axiosClient';
import { useCurrency } from '../../app/CurrencyContext';

interface Entry {
  id: string;
  currency: string;
  type: 'credit' | 'debit' | 'lock' | 'unlock';
  amount: number;
  reason: string;
  balanceAfter: number;
  createdAt: string;
}

interface Hold {
  id: string;
  currency: string;
  kind: 'principal' | 'roi' | 'override';
  amount: number;
  releaseAt: string | null;
}

const REASONS: Record<string, string> = {
  deposit_approved: 'Deposit approved',
  portal_principal: 'Portal investment added (locked)',
  portal_principal_cancelled: 'Portal investment cancelled',
  portal_roi_credit: 'Portal ROI profit credited',
  rebate_direct: 'Direct bonus claimed',
  rebate_override: 'Team override bonus claimed',
  rank_reward: 'Rank reward claimed',
  brand_club: 'Brand Club profit share claimed',
  milestone_bonus: 'Big investment bonus claimed',
  withdrawal_request: 'Withdrawal requested',
  withdrawal_approved: 'Withdrawal paid',
  withdrawal_rejected: 'Withdrawal rejected, amount returned',
  withdrawal_cancelled: 'Withdrawal cancelled, amount returned',
};

const HOLD_LABELS: Record<string, string> = {
  principal: 'Portal investment (comes back through monthly ROI)',
  roi: 'Monthly ROI (30-day wait)',
  override: 'Team override bonus (30-day wait)',
};

const TYPE_LABELS: Record<string, string> = {
  credit: 'Added',
  debit: 'Paid out',
  lock: 'Reserved',
  unlock: 'Returned',
};

const reasonLabel = (reason: string) =>
  REASONS[reason] || reason.replace(/_/g, ' ');

const istDate = (value: string | null) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const istDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const box: React.CSSProperties = {
  marginTop: 32,
  padding: 20,
  borderRadius: 14,
  border: '1px solid var(--line)',
  background: 'var(--card-bg)',
};

const row: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 16,
  padding: '10px 0',
  borderBottom: '1px solid var(--line)',
  fontSize: 13.5,
};

const sub: React.CSSProperties = {
  fontSize: 11.5,
  color: 'var(--muted)',
  marginTop: 2,
};

const WalletHistory: React.FC = () => {
  const { formatFromNative } = useCurrency();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [holds, setHolds] = useState<Hold[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;

    const load = () => {
      axiosClient.get('/funds/wallet-history')
        .then(({ data }) => {
          if (!active) return;
          setEntries(Array.isArray(data.entries) ? data.entries : []);
          setHolds(Array.isArray(data.holds) ? data.holds : []);
          setFailed(false);
        })
        .catch(() => {
          if (active) setFailed(true);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    };

    load();
    window.addEventListener('wallet:refresh', load);

    return () => {
      active = false;
      window.removeEventListener('wallet:refresh', load);
    };
  }, []);

  return (
    <div style={box}>
      <h3 style={{ margin: '0 0 4px' }}>Wallet Activity</h3>
      <div style={sub}>
        Every rupee that came into or left your wallet, and what is locked right now.
      </div>

      {loading && <div style={{ ...sub, marginTop: 14 }}>Loading…</div>}
      {failed && !loading && (
        <div style={{ ...sub, marginTop: 14 }}>Could not load wallet activity.</div>
      )}

      {!loading && !failed && holds.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <b style={{ fontSize: 13 }}>Locked right now</b>
          {holds.map((hold) => (
            <div key={hold.id} style={row}>
              <div>
                {HOLD_LABELS[hold.kind] || hold.kind}
                <div style={sub}>
                  {hold.releaseAt
                    ? `Withdrawable from ${istDate(hold.releaseAt)}`
                    : 'Stays locked until it is paid back through ROI'}
                </div>
              </div>
              <b>{formatFromNative(hold.amount, hold.currency)}</b>
            </div>
          ))}
        </div>
      )}

      {!loading && !failed && (
        <div style={{ marginTop: 16 }}>
          <b style={{ fontSize: 13 }}>History</b>
          {entries.length === 0 && (
            <div style={{ ...sub, marginTop: 8 }}>No wallet activity yet.</div>
          )}
          {entries.map((entry) => (
            <div key={entry.id} style={row}>
              <div>
                {reasonLabel(entry.reason)}
                <div style={sub}>{istDateTime(entry.createdAt)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <b
                  style={{
                    color: entry.type === 'credit' || entry.type === 'unlock'
                      ? 'var(--success)'
                      : 'var(--warning)',
                  }}
                >
                  {entry.type === 'credit' || entry.type === 'unlock' ? '+' : '−'}
                  {formatFromNative(entry.amount, entry.currency)}
                </b>
                <div style={sub}>{TYPE_LABELS[entry.type] || entry.type}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WalletHistory;